import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { PdfViewerModal } from '../components/PdfViewerModal';
import { CatalogoCard } from '../components/catalogos/CatalogoCard';
import { UploadCatalogoModal } from '../components/catalogos/UploadCatalogoModal';
import { BookOpen, Plus, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = [['all', 'Todos'], ['catalogo', 'Catálogos'], ['apresentacao', 'Apresentações']];

export default function Catalogos() {
  const { isAdmin, canViewCatalogos } = useAuth();
  const allowed = !!canViewCatalogos();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/catalogos`);
      setItems(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar arquivos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (allowed) load(); }, [load, allowed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => (tab === 'all' || i.category === tab)
      && (!q || i.title.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q)));
  }, [items, tab, search]);

  if (!allowed) return <Navigate to="/dashboard" replace />;

  const handleDownload = async (item) => {
    try {
      const res = await axios.get(`${API_URL}/api/catalogos/${item.id}/file?download=true`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/catalogos/${deleting.id}`);
      toast.success('Arquivo excluído');
      setItems((prev) => prev.filter((i) => i.id !== deleting.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    } finally {
      setDeleting(null);
    }
  };

  const countFor = (key) => (key === 'all' ? items.length : items.filter((i) => i.category === key).length);

  return (
    <Layout>
      <div className="space-y-6" data-testid="catalogos-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-blue-700" />
              Catálogos e Apresentações
            </h1>
            <p className="text-slate-500 text-sm mt-1">Materiais comerciais em PDF para consulta da equipe de Orçamentos.</p>
          </div>
          {isAdmin() && (
            <Button onClick={() => setUploadOpen(true)} data-testid="catalogo-upload-btn">
              <Plus className="w-4 h-4 mr-2" />Enviar PDF
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex bg-white border border-slate-200 rounded-md p-1" data-testid="catalogo-tabs">
            {TABS.map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`catalogo-tab-${key}`}
                className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${tab === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                {label} <span className="opacity-60 ml-1">{countFor(key)}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Buscar por título ou descrição" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="catalogo-search-input" />
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-lg" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-lg" data-testid="catalogo-empty">
            <BookOpen className="w-10 h-10 mx-auto text-slate-300" />
            <p className="text-slate-500 mt-3">Nenhum arquivo encontrado.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="catalogo-list">
            {filtered.map((item) => (
              <CatalogoCard key={item.id} item={item} canManage={isAdmin()}
                onView={setViewing} onDownload={handleDownload} onDelete={setDeleting} />
            ))}
          </div>
        )}
      </div>

      <UploadCatalogoModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load}
        defaultCategory={tab === 'apresentacao' ? 'apresentacao' : 'catalogo'} key={uploadOpen ? tab : 'closed'} />

      <PdfViewerModal open={!!viewing} onClose={() => setViewing(null)}
        pdfUrl={viewing ? `${API_URL}/api/catalogos/${viewing.id}/file` : null}
        filename={viewing?.filename} title={viewing?.title} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="catalogo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir arquivo?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.title}" será removido permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="catalogo-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="catalogo-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
